"use client";

import Link from "next/link";

interface DownloadResultProps {
  fileName: string;
  fileSize: number;
  downloadUrl: string;
  onReset: () => void;
}

export default function DownloadResult({ fileName, fileSize, downloadUrl, onReset }: DownloadResultProps) {
  const sizeMB = (fileSize / 1024 / 1024).toFixed(2);

  return (
    <div className="flex flex-col items-center gap-6 py-8">
      {/* Success checkmark */}
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
        <svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      </div>

      <div className="text-center">
        <h3 className="text-lg font-bold text-slate-900">Done!</h3>
        <p className="mt-1 text-sm text-slate-500">
          {fileName} <span className="text-slate-400">({sizeMB} MB)</span>
        </p>
      </div>

      {/* Download button */}
      <a
        href={downloadUrl}
        download={fileName}
        className="btn-primary px-8 py-3 text-base"
      >
        <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
        Download
      </a>

      {/* Reset button */}
      <button
        onClick={onReset}
        className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2 transition-colors"
      >
        Process another file
      </button>

      {/* CTA */}
      <div className="mt-4 w-full max-w-md rounded-xl border border-slate-200 bg-slate-50 p-5 text-center">
        <p className="text-sm font-medium text-slate-700">
          Need to send this for signature?
        </p>
        <p className="mt-1 text-xs text-slate-400">
          Upload, place signature fields, send — get a legally signed PDF back.
        </p>
        <Link
          href="/register"
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-ink)] px-5 py-2 text-sm font-semibold text-white hover:bg-[var(--color-ink-light)] transition-colors"
        >
          Try SignForge — it&apos;s free
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        </Link>
      </div>
    </div>
  );
}
